import { MutableRecordStore } from '@deqo/foundation';
import { Component, ObjectManager } from '../../../concepts/objects';
import { StoreObserver, StoreState } from '../../../concepts/revive/Store';
import { ActiveObject, resolve } from '../../Container';
import { ProfileModel } from '../../ProfileModel';
import { Participant } from '../../canvas/participants/Participant';
import { ParticipantManager } from '../../canvas/participants/ParticipantManager';
import { PreferencesModel } from '../../preferences/PreferencesModel';
import { HandwritingMappingsAttr, HandwritingModeAttr, Handwritten } from './Handwritten';

class HandwritingMappingStore extends MutableRecordStore({} as any) {

    public get(userId: string) {
        return this.state[userId];
    }

    public set(userId: string, value: string) {
        this.state[userId] = value;
    }
}

export class HandwritingMechanics extends ActiveObject {

    @resolve(ObjectManager) private objects!: ObjectManager;
    @resolve(ProfileModel) private profile!: ProfileModel;
    @resolve(PreferencesModel) private preferences!: PreferencesModel;
    @resolve(ParticipantManager) private participants!: ParticipantManager;

    private mappings = new HandwritingMappingStore();

    public start = () => {
        this.burden(
            this.preferences.observe(this.onPreferencesChanged),
            this.profile.observe(this.onProfileChanged),
            this.participants.on('*', x => this.updateMappingsIfApplicable(x)),
            this.mappings.observe(() => this.syncAll()),
            this.objects.on('component', 'allocated', x => this.sync(x)),
        );

        this.mappings.mutateTo(Object.fromEntries(this.participants.map(x => [x.id, x.handwriting])));
        this.syncAll();
    };

    private onPreferencesChanged: StoreObserver<StoreState<PreferencesModel>> = (next, prev) => {
        if (next.handwritingMode !== prev.handwritingMode) {
            this.syncAll();
        }
    };

    private onProfileChanged: StoreObserver<StoreState<ProfileModel>> = (next, prev) => {
        if (next.handwriting !== prev.handwriting) {
            this.participants.getSelf()?.update({ handwriting: next.handwriting });
        }
    };

    private updateMappingsIfApplicable({ id, handwriting }: Participant) {
        if (this.mappings.get(id) !== handwriting) {
            this.mappings.mutate(m => void (m[id] = handwriting));
        }
    }

    private syncAll = () => {
        const all = this.objects.getAll(Handwritten);
        this.sync(Array.from(all));
        this.objects.touch(Array.from(all));
    };

    private sync = (cs: Component[]) => {
        for (const c of cs) {
            c.as(Handwritten, h => {
                HandwritingModeAttr.setMaybe(h, this.preferences.handwritingMode);
                HandwritingMappingsAttr.setMaybe(h, this.mappings.getState());
            });
        }
    };
}
